import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { get, size } from "lodash";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useAreaForm } from "../../data/talons/Area/useAreaForm";
import { color } from "../../utils/constant";
import { useSelector } from "react-redux";
import { getAreaData } from "../../data/selectors/Area/areaform.selector";

/*
 * Render Real - Estate Inquiry Form screen
 *
 *
 */

export default function AreaForm(props) {
  const { enqueueSnackbar } = useSnackbar();
  const editItem = useSelector(getAreaData);
  const {
    loader,
    loading,
    status,
    areaEntrySchema,
    handleStatusComboChange,
    areaEntry,
    areaUpdate,
  } = useAreaForm({
    existingStatus: get(editItem, "status", 0),
  });

  const defaultValues = useMemo(
    () => ({
      name: get(editItem, "name", ""),
      address: get(editItem, "address", ""),
      location_url: get(editItem, "location_url", ""),
      latitude: get(editItem, "latitude", ""),
      longitude: get(editItem, "longitude", ""),
    }),
    [editItem]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: joiResolver(areaEntrySchema),
    defaultValues,
  });

  const onUserSubmit = useCallback(async () => {
    try {
      const res =
        get(editItem, "id", null) === null
          ? await areaEntry({
              ...getValues(),
            })
          : await areaUpdate({
              ...getValues(),
              id: get(editItem, "id", null),
            });
      if (res === true) {
        props.postSubmit();
      } else {
        enqueueSnackbar("Problem While Saving Area Data", {
          variant: "error",
        });
      }
    } catch (err) {
      console.log("errr", err);
    }
  }, [areaEntry, areaUpdate, editItem, enqueueSnackbar, getValues, props]);

  const onlyCharacters = (e) => {
    e.target.value = e.target.value.replace(/[^a-zA-Z ]*$/, "");

    return e.target.value;
  };

  return (
    <React.Fragment>
      <Typography variant="h5" gutterBottom>
        Area Form
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <TextField
            required
            id="name"
            name="name"
            label="Area Name"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("name")}
            autoComplete="false"
            error={size(errors?.name?.message) > 0 ? true : false}
            helperText={
              size(errors?.name?.message) > 0 ? errors?.name?.message : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            id="address"
            name="address"
            label="Address"
            multiline
            rows={3}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("address")}
            autoComplete="false"
            error={size(errors?.address?.message) > 0 ? true : false}
            helperText={
              size(errors?.address?.message) > 0 ? errors?.address?.message : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            id="location_url"
            name="location_url"
            label="Location URL"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("location_url")}
            autoComplete="false"
            error={size(errors?.location_url?.message) > 0 ? true : false}
            helperText={
              size(errors?.location_url?.message) > 0
                ? errors?.location_url?.message
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            id="latitude"
            name="latitude"
            label="Latitude"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("latitude")}
            autoComplete="false"
            error={size(errors?.latitude?.message) > 0 ? true : false}
            helperText={
              size(errors?.latitude?.message) > 0
                ? errors?.latitude?.message
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            id="longitude"
            name="longitude"
            label="Longitude"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("longitude")}
            autoComplete="false"
            error={size(errors?.longitude?.message) > 0 ? true : false}
            helperText={
              size(errors?.longitude?.message) > 0
                ? errors?.longitude?.message
                : ""
            }
          />
        </Grid>

        <Grid item xs={3} sm={3}>
          <Typography>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Checkbox
                  sx={{
                    "&.Mui-checked": {
                      color: color.button,
                    },
                  }}
                  id="is_labour"
                  checked={status}
                  onChange={handleStatusComboChange}
                />
              }
              label="Active"
            />
          </Typography>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12}>
            <Stack direction="row" justifyContent="end">
              <Button
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  backgroundColor: color.button,
                  "&:hover": {
                    backgroundColor: color.button,
                  },
                }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(onUserSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  borderColor: color.button,
                  color: color.button,
                  "&:hover": {
                    color: color.button,
                  },
                }}
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  props.postSubmit();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
